import React from "react";
import { connect } from "react-redux";
import {
    Tooltip,
    WithStyles,
    withStyles,
    ListItem,
    Grid,
    Button,
} from "material-ui";
import InfoOutlineIcon from "material-ui-icons/InfoOutline";
import moment from "moment";
import { ReduxState, ReduxDispatch } from "../../ducks/reducers";
import AlternatingList from "../../components/AlternatingList";
import { LoanPieChart } from "../../components/LoanPieChart/LoanPieChart";
import { PaymentPlanPlot } from "../../components/PaymentPlanPlot/PaymentPlanPlot";
import { ShowLogo, SetHeader } from "../../ducks/common";
import { getLoanData, emptyLoanData, editLoanName } from "../../ducks/loan";
import { LoanDto, InstalmentDto } from "../../util/DTOs/LoanDtos";
import { RouterProps } from "react-router";
import { formatNumber, expandReferenceRate } from "../../util/utils";
import { EditableHeader } from "../../components/EditableHeader/EditableHeader";
import { PaymentPlanDto } from "../../util/DTOs/PaymentPlanDtos";
import CollapsibleTable from "../../components/CollapsibleTable";

const styles = {
    container: {
        paddingRight: 15,
        paddingLeft: 15,
        marginBottom: 60,
    },
    subheadingWeight: {
        fontWeight: "200",
    },
    centerText: {
        textAlign: "center",
    },
    rightText: {
        textAlign: "right",
    },
    flexRoot: {
        display: "flex",
        width: "100%",
    },
    flexItem: {
        flex: 1,
    },
} as React.CSSProperties;

interface ILoanPageProps {
    id: number;
    isVisibleView: boolean;
}

interface ILoanPageDispatchProps {
    setHeaderValue: (header: string) => void;
    showLogo: (show: boolean) => void;
    saveLoanName: (id: number, loanNumber: string, loanName: string) => void;
}
interface ILoanPageReduxProps {
    loanData: LoanDto;
}

interface LoanPageState {
    windowWidth: number;
    showPaymentPlanAsTable: boolean;
}

type LoanPageProps = ILoanPageProps &
    ILoanPageReduxProps &
    ILoanPageDispatchProps &
    RouterProps;

type LoanPagePropsWithStyles = LoanPageProps &
    WithStyles<
        | "container"
        | "subheadingWeight"
        | "centerText"
        | "rightText"
        | "flexRoot"
        | "flexItem"
    >;

class LoanPage extends React.Component<LoanPagePropsWithStyles, LoanPageState> {
    constructor(props: LoanPagePropsWithStyles) {
        super(props);

        this.state = {
            windowWidth: this.calculateWindowSize(),
            showPaymentPlanAsTable: false,
        };
    }

    windowResize = () => {
        this.setState((prevState) => ({
            ...prevState,
            windowWidth: this.calculateWindowSize(),
        }));
    };

    calculateWindowSize() {
        return window.innerWidth;
    }

    componentDidMount() {
        window.addEventListener("resize", this.windowResize);
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.windowResize);
    }

    saveLoanName = (value: string) => {
        // Save edited loan name
        this.props.saveLoanName(
            this.props.loanData.id,
            this.props.loanData.loanNumber,
            value
        );
    };

    renderListItems = (items: InstalmentDto[]) => {
        let elements: JSX.Element[] = [];

        const renderableItems = items.filter((i) => {
            const comparisonDate = new Date(2019, 0, 1);
            const instalmentDate = new Date(i.dueDate);
            return instalmentDate > comparisonDate;
        });

        renderableItems.forEach((item, index) => {
            const elem = (
                <ListItem key={index}>
                    <div className={`${this.props.classes.flexRoot}`}>
                        <span className="hypodgrey" style={{ marginRight: 15 }}>
                            {moment(item.dueDate).format("DD.MM.YYYY")}
                        </span>
                        <span
                            className={`hypodgrey fade ${this.props.classes.flexItem}`}
                            style={{ marginRight: 15 }}
                        >
                            {item.status}
                            {item.isLate && (
                                <span
                                    className="payment-late"
                                    style={{ marginLeft: "25%" }}
                                >
                                    Rästissä
                                </span>
                            )}
                        </span>
                        <span
                            className={`sum ${
                                item.isLate
                                    ? "payment-late"
                                    : item.instalmentSum > 0.0
                                    ? "payment-negative"
                                    : "payment"
                            }
                            ${this.props.classes.rightText}`}
                        >
                            {`${formatNumber(item.instalmentSum, 2)} €`}
                        </span>
                    </div>
                </ListItem>
            );

            elements.push(elem);
        });

        return elements;
    };

    render() {
        const { classes, loanData } = this.props;

        const paymentPlanPlotWidth =
            this.state.windowWidth > 650 ? 600 : this.state.windowWidth - 40;

        let fontSize =
            loanData && formatNumber(loanData.paymentAmount || 0, 2).length > 6
                ? 20
                : 25;
        if (this.state.windowWidth > 420) {
            fontSize = 30;
        }

        let nextPayment: PaymentPlanDto | undefined = undefined;

        nextPayment =
            loanData.paymentPlan && loanData.paymentPlan.length > 0
                ? loanData.paymentPlan.find(
                      (p) => new Date(p.dueDate) > new Date()
                  )
                : undefined;

        const lastInstalmentDateStr =
            loanData.paymentPlan && loanData.paymentPlan.length > 0
                ? moment(
                      loanData.paymentPlan[loanData.paymentPlan.length - 1]
                          .dueDate
                  ).format("DD.MM.YYYY")
                : "-";

        return (
            <Grid
                style={{ width: "100%", marginLeft: 0, marginRight: 0 }}
                container
                className={`${classes.container}`}
            >
                <Grid item xs={12} style={{ paddingRight: 0, paddingLeft: 0 }}>
                    <Grid item id="general-info">
                        <EditableHeader
                            saveValue={this.saveLoanName}
                            value={loanData.loanName}
                            headerType={"h2"}
                            maxLength={50}
                            label={"Muokkaa lainan nimeä"}
                            isVisible={this.props.isVisibleView}
                        />
                        <p className={`hypodgrey no-margin`}>
                            {loanData.loanTypeName} #{loanData.loanNumber}
                        </p>
                    </Grid>

                    <Grid item id="loan-balance">
                        <h3
                            className={`hypodgrey no-margin ${classes.centerText}`}
                        >
                            Lainan saldo
                            {(loanData.currentDate &&
                                ` (${moment(loanData.currentDate).format(
                                    "D.M."
                                )})`) ||
                                ``}
                            :
                        </h3>
                        <p
                            className={`loan-primary-info no-margin ${classes.centerText}`}
                        >
                            {formatNumber(
                                loanData.currentOutstandingPrincipalAmount,
                                2
                            )}{" "}
                            €
                        </p>

                        <h3
                            className={`hypodgrey no-margin ${classes.centerText}`}
                        >
                            Maksetut lyhennykset:
                        </h3>
                        <p
                            className={`loan-secondary-info no-margin ${classes.centerText}`}
                        >
                            {formatNumber(loanData.repaymentAmount)} €
                        </p>
                    </Grid>

                    {/* Loan Chart */}
                    {loanData.repaymentAmount !== undefined && (
                        <Grid container justify="center" id="loan-chart">
                            <LoanPieChart
                                width={200}
                                height={200}
                                paidAmount={loanData.repaymentAmount || 0}
                                unPaidAmount={
                                    loanData.currentOutstandingPrincipalAmount ||
                                    0
                                }
                                donutInset={20}
                                donutThickness={10}
                            />
                        </Grid>
                    )}
                    {/* Basic info */}
                    <Grid
                        style={{ width: "100%", marginLeft: 0 }}
                        container
                        direction="row"
                        justify="center"
                        id="loan-info-box"
                    >
                        <Grid container>
                            <Grid
                                xs={6}
                                item
                                className="loan-info-top left left-column"
                            >
                                <h2
                                    className={`hypodgrey no-margin ${classes.subheadingWeight}`}
                                >
                                    Korko
                                </h2>
                                <p
                                    style={{ fontSize: fontSize }}
                                    className="loan-primary-info no-margin"
                                >
                                    {loanData.interestInfo
                                        .interestRateTotalError === true ? (
                                        "Virhe"
                                    ) : (
                                        <>
                                            {formatNumber(
                                                loanData.interestInfo
                                                    .interestRateTotalPct,
                                                3
                                            )}{" "}
                                            %
                                        </>
                                    )}
                                </p>
                            </Grid>
                            <Grid
                                item
                                xs={6}
                                className="loan-info-top right right-column"
                            >
                                <h2
                                    className={`hypodgrey no-margin ${classes.subheadingWeight}`}
                                >
                                    Maksuerä
                                </h2>
                                <p
                                    style={{ fontSize: fontSize }}
                                    className="loan-primary-info no-margin"
                                >
                                    {nextPayment
                                        ? formatNumber(
                                              nextPayment.paymentAmount,
                                              2
                                          )
                                        : "-"}{" "}
                                    €
                                </p>
                            </Grid>
                            <Grid item xs={6} className="left-column" />
                            <Grid
                                className="no-padding right-column"
                                item
                                xs={6}
                                style={{ paddingRight: 0 }}
                            >
                                {nextPayment && (
                                    <span>
                                        <p className="hypodgrey no-margin left">
                                            Lyhennys:{" "}
                                        </p>
                                        <p className="hypodgrey no-margin right">
                                            <span className="highlight">
                                                {nextPayment
                                                    ? formatNumber(
                                                          nextPayment.instalmentAmount,
                                                          2
                                                      )
                                                    : "0"}{" "}
                                                €
                                            </span>
                                        </p>
                                    </span>
                                )}
                            </Grid>
                            <Grid
                                className="no-padding left-column"
                                xs={6}
                                item
                            >
                                <p
                                    className={`hypodgrey fade ${classes.flexItem} no-margin left`}
                                    style={{ textAlign: "end" }}
                                >
                                    {loanData.interestInfo.referenceRate !=
                                        null && (
                                        <>
                                            {expandReferenceRate(
                                                loanData.interestInfo
                                                    .referenceRate
                                            )}
                                            {!loanData.interestInfo.referenceRate.includes(
                                                "Kiinteä"
                                            ) && <>: </>}
                                        </>
                                    )}
                                </p>
                                <p className="hypodgrey no-margin right">
                                    {loanData.interestInfo
                                        .interestRateBasePct != null &&
                                        loanData.interestInfo.referenceRate !=
                                            null &&
                                        !loanData.interestInfo.referenceRate.includes(
                                            "Kiinteä"
                                        ) && (
                                            <span className="highlight">
                                                {formatNumber(
                                                    loanData.interestInfo
                                                        .interestRateBasePct,
                                                    3
                                                )}{" "}
                                                %
                                            </span>
                                        )}
                                </p>
                            </Grid>
                            <Grid
                                className="no-padding right-column"
                                item
                                xs={6}
                            >
                                {(nextPayment && (
                                    <span>
                                        <p className="hypodgrey no-margin left">
                                            Korko:{" "}
                                        </p>
                                        <p className="hypodgrey no-margin right">
                                            <span className="highlight">
                                                {nextPayment
                                                    ? formatNumber(
                                                          nextPayment.interestAmount,
                                                          2
                                                      )
                                                    : "0"}{" "}
                                                €
                                            </span>
                                        </p>
                                    </span>
                                )) || (
                                    <p
                                        className="hypodgrey no-margin left"
                                        style={{
                                            position: "absolute",
                                            width: 100,
                                        }}
                                    >
                                        Seuraava maksuerä muodostuu 2-3 päivää
                                        ennen eräpäivää.
                                    </p>
                                )}
                            </Grid>
                            <Grid
                                className="no-padding left-column"
                                xs={6}
                                item
                            >
                                {loanData.interestInfo.interestMarginPct !=
                                    null &&
                                    loanData.interestInfo.referenceRate !=
                                        null &&
                                    !loanData.interestInfo.referenceRate.includes(
                                        "Kiinteä"
                                    ) && (
                                        <>
                                            <p className="hypodgrey no-margin left">
                                                Marginaali:{" "}
                                            </p>
                                            <p className="hypodgrey no-margin right">
                                                {loanData.interestInfo
                                                    .interestMarginPct !=
                                                    null && (
                                                    <span className="highlight">
                                                        {formatNumber(
                                                            loanData
                                                                .interestInfo
                                                                .interestMarginPct,
                                                            3
                                                        )}{" "}
                                                        %
                                                    </span>
                                                )}
                                            </p>
                                        </>
                                    )}
                            </Grid>
                            <Grid
                                className="no-padding right-column"
                                item
                                xs={6}
                            >
                                {nextPayment && (
                                    <span>
                                        <p className="hypodgrey no-margin left">
                                            Palkkiot:{" "}
                                        </p>
                                        <p className="hypodgrey no-margin right">
                                            <span className="highlight">
                                                {nextPayment
                                                    ? formatNumber(
                                                          nextPayment.expenses,
                                                          2
                                                      )
                                                    : "0"}{" "}
                                                €
                                            </span>
                                        </p>
                                        <Tooltip
                                            disableTriggerTouch
                                            title={
                                                "Näytettävät palkkiot eivät sisällä erillistoimenpiteistä veloitettavia tai erikseen sovittuja palveluhinnaston mukaisia kuluja tai palkkioita."
                                            }
                                            aria-label={
                                                "Näytettävät palkkiot eivät sisällä erillistoimenpiteistä veloitettavia tai erikseen sovittuja palveluhinnaston mukaisia kuluja tai palkkioita."
                                            }
                                            classes={{
                                                tooltip: "expenseinfo-tooltip",
                                                root: "expenseinfo-root",
                                            }}
                                            onClick={() => {}}
                                            placement={"bottom-start"}
                                        >
                                            <InfoOutlineIcon />
                                        </Tooltip>
                                    </span>
                                )}
                            </Grid>
                            <Grid
                                className="no-padding left-column"
                                item
                                xs={6}
                            />
                            <Grid
                                className="no-padding right-column"
                                item
                                xs={6}
                            >
                                {nextPayment && (
                                    <span>
                                        <p className="hypodgrey no-margin left">
                                            Muut:{" "}
                                        </p>
                                        <p className="hypodgrey no-margin right">
                                            <span className="highlight">
                                                {nextPayment
                                                    ? formatNumber(
                                                          nextPayment.latePaymentInterest,
                                                          2
                                                      )
                                                    : "0"}{" "}
                                                €
                                            </span>
                                        </p>
                                    </span>
                                )}
                            </Grid>
                            <Grid
                                xs={6}
                                item
                                className="loan-info-bottom left-column"
                                style={{ paddingRight: 8, paddingLeft: 0 }}
                            >
                                <p
                                    style={{
                                        fontSize: "0.8em",
                                        textAlign: "right",
                                    }}
                                    className="hypodgrey no-margin"
                                >
                                    Seuraava tarkistus:
                                </p>
                                <h3
                                    style={{ textAlign: "right" }}
                                    className="hypolblue no-margin"
                                >
                                    {loanData.interestInfo.nextInterestRateCheck
                                        ? moment(
                                              loanData.interestInfo
                                                  .nextInterestRateCheck
                                          ).format("DD.MM.YYYY")
                                        : "-"}
                                </h3>
                            </Grid>
                            <Grid
                                item
                                xs={6}
                                className="loan-info-bottom right-column"
                            >
                                {nextPayment && (
                                    <span>
                                        <p
                                            style={{
                                                fontSize: "0.8em",
                                                textAlign: "left",
                                            }}
                                            className="hypodgrey no-margin"
                                        >
                                            Seuraava eräpäivä:
                                        </p>
                                        <h3
                                            style={{ textAlign: "left" }}
                                            className="hypolblue no-margin"
                                        >
                                            {loanData.nextPaymentDate
                                                ? moment(
                                                      loanData.nextPaymentDate
                                                  ).format("DD.MM.YYYY")
                                                : ""}
                                        </h3>
                                    </span>
                                )}
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* Payment events */}
                    <Grid item id="payment-events">
                        <Grid style={{ position: "relative" }}>
                            <h2
                                className={`hypodgrey ${classes.subheadingWeight}`}
                            >
                                Maksutapahtumat
                            </h2>
                        </Grid>
                        {(loanData &&
                            loanData.instalments &&
                            loanData.instalments.length > 0 && (
                                <AlternatingList>
                                    {this.renderListItems(loanData.instalments)}
                                </AlternatingList>
                            )) || (
                            <h3
                                style={{ marginLeft: 15 }}
                                className={`hypoblue ${classes.subheadingWeight} no-margin`}
                            >
                                Ei tapahtumia
                            </h3>
                        )}
                    </Grid>

                    {/* Payment plan */}
                    <Grid item id="payment-plan">
                        <Grid item style={{ position: "relative" }}>
                            <h2
                                className={`hypodgrey ${classes.subheadingWeight}`}
                            >
                                Maksusuunnitelma
                                <Tooltip
                                    disableTriggerTouch
                                    style={{
                                        position: "absolute",
                                        right: 0,
                                        top: 5,
                                        color: "#004170",
                                    }}
                                    title={
                                        "Maksusuunnitelma-graafissa on esitetty lainasi maksut siten kun ne tapahtuisivat nykytietojen ja korkojen mukaan ilman poikkeuksia. " +
                                        "Todellisuudessa muuttuvat korot ja ylimääräiset lyhennykset, lyhennysvapaat yms vaikuttavat siihen, miten maksusuunnitelma toteutuu."
                                    }
                                    aria-label={
                                        "Maksusuunnitelma-graafissa on esitetty lainasi maksut siten kun ne tapahtuisivat nykytietojen ja korkojen mukaan ilman poikkeuksia. " +
                                        "Todellisuudessa muuttuvat korot ja ylimääräiset lyhennykset, lyhennysvapaat yms vaikuttavat siihen, miten maksusuunnitelma toteutuu."
                                    }
                                    classes={{
                                        tooltip: "loanpage-tooltip",
                                        popper: "loanpage-popper",
                                    }}
                                    placement={"bottom-start"}
                                    onClick={() => {}}
                                >
                                    <InfoOutlineIcon />
                                </Tooltip>
                            </h2>
                        </Grid>
                        {loanData.paymentPlan &&
                        loanData.paymentPlan.length > 0 ? (
                            <>
                                <Grid
                                    item
                                    xs={12}
                                    md={12}
                                    style={{ marginBottom: 20 }}
                                >
                                    <Button
                                        id="showPaymentPlanAsBtn"
                                        color="primary"
                                        fullWidth={true}
                                        variant="flat"
                                        onClick={() =>
                                            this.setState({
                                                showPaymentPlanAsTable: !this
                                                    .state
                                                    .showPaymentPlanAsTable,
                                            })
                                        }
                                        style={{ minHeight: "54px" }}
                                        className={
                                            "show-payment-plan-as-button"
                                        }
                                    >
                                        Näytä{" "}
                                        {this.state.showPaymentPlanAsTable
                                            ? "graafina"
                                            : "taulukkona"}
                                    </Button>
                                </Grid>
                                {this.state.showPaymentPlanAsTable ? (
                                    <Grid
                                        style={{
                                            height:
                                                (3 / 4) * paymentPlanPlotWidth +
                                                4,
                                            overflow: "auto",
                                        }}
                                    >
                                        <CollapsibleTable
                                            data={loanData.paymentPlan}
                                        />
                                    </Grid>
                                ) : (
                                    <Grid
                                        aria-label={
                                            "Alkuperäinen lainasumma " +
                                            loanData.principalAmount +
                                            " €, nykyinen saldo " +
                                            loanData.currentOutstandingPrincipalAmount +
                                            " €, maksuerä " +
                                            (nextPayment
                                                ? nextPayment.paymentAmount +
                                                  " €"
                                                : "ei saatavilla") +
                                            ", viimeinen lyhennys " +
                                            lastInstalmentDateStr
                                        }
                                        item
                                        style={{
                                            display: "flex",
                                            justifyContent: "center",
                                            marginBottom: 8,
                                        }}
                                    >
                                        <div aria-hidden={true}>
                                            <PaymentPlanPlot
                                                height={
                                                    (3 / 4) *
                                                    paymentPlanPlotWidth
                                                }
                                                width={paymentPlanPlotWidth}
                                                paymentPlanData={{
                                                    currentDateStr:
                                                        loanData.currentDate ||
                                                        "1900-01-01",
                                                    currentBalance:
                                                        loanData.currentOutstandingPrincipalAmount ||
                                                        100000,
                                                    originalBalance:
                                                        loanData.principalAmount ||
                                                        100000,
                                                    paymentAmount:
                                                        loanData.paymentAmount ||
                                                        1000,
                                                    firstDisbursementDateStr:
                                                        loanData.firstDisbursementDate,
                                                    futurePlan:
                                                        loanData.paymentPlan,
                                                }}
                                                loanId={loanData.id}
                                            />
                                        </div>
                                    </Grid>
                                )}
                            </>
                        ) : (
                            <h3
                                style={{ marginLeft: 15 }}
                                className={`hypoblue ${classes.subheadingWeight} no-margin`}
                            >
                                Ei maksusuunnitelmaa
                            </h3>
                        )}
                    </Grid>
                    <Grid item id="basic-info">
                        <Grid item>
                            <h2
                                className={`hypodgrey ${classes.subheadingWeight}`}
                            >
                                Perustiedot
                            </h2>
                        </Grid>
                        <Grid item>
                            <h3
                                className="hypodgrey no-margin"
                                style={{ fontSize: 16 }}
                            >
                                Alkuperäinen lainamäärä:
                            </h3>
                            <h2
                                className="hypoblue no-margin"
                                style={{ marginLeft: 15 }}
                            >
                                {formatNumber(loanData.principalAmount, 2)} €
                            </h2>
                        </Grid>
                        <Grid item>
                            <h3
                                className="hypodgrey no-margin"
                                style={{ fontSize: 16 }}
                            >
                                Nostopäivä:
                            </h3>
                            <h2
                                className="hypoblue no-margin"
                                style={{ marginLeft: 15 }}
                            >
                                {loanData.firstDisbursementDate
                                    ? moment(
                                          loanData.firstDisbursementDate
                                      ).format("DD.MM.YYYY")
                                    : "-"}
                            </h2>
                        </Grid>
                        <Grid item>
                            <h3
                                className="hypodgrey no-margin"
                                style={{ fontSize: 16 }}
                            >
                                Velalliset:
                            </h3>
                            {loanData.debtors.map((debtor, i) => (
                                <h2
                                    key={i}
                                    className="hypoblue no-margin"
                                    style={{ marginLeft: 15 }}
                                >
                                    {debtor.name}
                                </h2>
                            ))}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        );
    }
}

const mapStateToProps = (
    state: ReduxState,
    props: LoanPageProps
): ILoanPageReduxProps => {
    const loanData = getLoanData(state, props.id) || emptyLoanData;
    return {
        loanData: loanData,
    };
};

const mapDispatchToProps = (
    dispatch: ReduxDispatch
): ILoanPageDispatchProps => {
    return {
        setHeaderValue: (header) => {
            return dispatch(SetHeader(header));
        },
        showLogo: (show: boolean) => {
            return dispatch(ShowLogo(show));
        },
        saveLoanName: (id: number, loanNumber: string, loanName: string) => {
            dispatch(editLoanName({ id, loanNumber, loanName }));
        },
    };
};

export default connect<
    ILoanPageReduxProps,
    ILoanPageDispatchProps,
    ILoanPageProps
>(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles)(LoanPage));



// WEBPACK FOOTER //
// ./src/views/loan/LoanPage.tsx